import { useCallback, useEffect, useRef } from 'react'
import { toast } from '@sales-center/components'
import { isEqual } from 'lodash'

import { useIsUnmounted } from '@sales-center/components'
import { useAppDispatch } from '@sales-center/common'
import {
  type BestFitRequest,
  searchAppointmentsByLocation,
} from '@user/store/scheduling'

import {
  useSchedulingWizard,
  setDataActionCreator as finishLoading,
  loadDataActionCreator as startLoading,
  prevScreenActionCreator as prevScreen,
} from '../../wizardContext'
import type { GetResultsHandler } from '../typings'

export function useGetResults() {
  const appDispatch = useAppDispatch()

  const [state, dispatch] = useSchedulingWizard()

  const { bestFitInput, externalSvcLocId, loading, response } = state

  const isUnmounted = useIsUnmounted()

  // track request args to avoid race conditions w/ result
  const rLatestRequest = useRef<BestFitRequest>()
  rLatestRequest.current = {
    externalSvcLocId,
    bestFitInput,
  }

  const allowDispatch = useCallback(
    (request: BestFitRequest) => {
      if (isUnmounted()) return false
      return isEqual(request, rLatestRequest.current)
    },
    [isUnmounted],
  )

  const getResults = useCallback<GetResultsHandler>(async () => {
    dispatch(startLoading())

    const request: BestFitRequest = {
      externalSvcLocId,
      bestFitInput,
    }

    try {
      const response = await appDispatch(searchAppointmentsByLocation(request))

      if (allowDispatch(request)) {
        if (searchAppointmentsByLocation.fulfilled.match(response)) {
          dispatch(finishLoading(response.payload))
        } else {
          toast.error('Error loading appointments')

          dispatch(
            finishLoading({
              items: [],
              count: 0,
              skip: 0,
              take: 0,
            }),
          )
        }
      }
    } catch {
      if (allowDispatch(request)) {
        dispatch(finishLoading())
        dispatch(prevScreen())
        toast.error('Unexpected error')
      }
    }
  }, [dispatch, externalSvcLocId, bestFitInput, appDispatch, allowDispatch])

  useEffect(() => {
    if (!loading && response?.count === 0) {
      dispatch(prevScreen())
    }
  }, [dispatch, loading, response?.count])

  return getResults
}
